import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
    Typography,
    Grid,
    Box,
    CssBaseline,
    TextField,
    Button,
    IconButton,
    Avatar,
    InputAdornment,
    Badge,
    Chip,
    Fade,
    Divider,
    Tooltip,
} from '@mui/material';
import {
    SearchRounded,
    SendRounded,
    SentimentSatisfiedAltRounded,
    DoneAllRounded,
    DoneRounded,
    AccessTimeRounded,
    ErrorOutlineRounded,
    ChatBubbleOutlineRounded,
    CloseRounded,
    PhoneRounded,                       // ← added
} from '@mui/icons-material';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import WebSocketContext from '../Context/WebSocket';
import { format, isToday, isYesterday, differenceInDays } from 'date-fns';
import EmojiPicker from 'emoji-picker-react';
import { Snackbar, Alert } from '@mui/material';

/* ── helpers ────────────────────────────────────────────────────────────── */
const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${Math.abs(hash) % 360}, 55%, 50%)`;
};

const initials = (name = '') =>
    name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

const Chat = () => {
    const [chatUsersData, setChatUsersData] = useState([]);
    const [searchUserValue, setSearchValue] = useState('');
    const [receipitentId, setReceipitentId] = useState('');
    const [userChatInfo, setUserChatInfo] = useState('');
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [state] = useState({ vertical: 'top', horizontal: 'center' });
    const { vertical, horizontal } = state;
    const { socket, user_id } = useContext(WebSocketContext);
    const api = axios.create({ baseURL: global_link });
    const authToken = process.env.AUTH_TOKEN;
    const authyToken = localStorage.getItem(authToken);
    const headers = {
        Authorization: `Bearer ${authyToken}`,
        'Content-Type': 'application/json',
    };

    const receipitentIdRef = useRef(receipitentId);
    useEffect(() => {
        receipitentIdRef.current = receipitentId;
    }, [receipitentId]);

    const getQueue = () => JSON.parse(localStorage.getItem('msg_queue') || '[]');
    const setQueue = (q) => localStorage.setItem('msg_queue', JSON.stringify(q));

    const handleEmojiClick = (emojiObject) => {
        setNewMessage((prev) => prev + emojiObject.emoji);
        // no setOpen(false) — picker stays open for rapid emoji adding
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const moveUserToTop = (userId) => {
        setChatUsersData((prev) => {
            const idx = prev.findIndex((u) => u.user_id === userId);
            if (idx > -1) {
                const updated = [...prev];
                const [moved] = updated.splice(idx, 1);
                return [{ ...moved, unreadCount: (moved.unreadCount || 0) + 1 }, ...updated];
            }
            return prev;
        });
    };

    const fetchUsersData = async (searchValue = '') => {
        try {
            const response = await api.get('/user/chatusers/', {
                params: { search: searchValue },
                headers,
            });
            const users = response.data;
            const usersWithUnread = await Promise.all(
                users.map(async (u) => {
                    try {
                        const r = await api.get(`/chat/unreadcount/${u.user_id}`, { headers });
                        return { ...u, unreadCount: r.data.unread_count || 0 };
                    } catch {
                        return { ...u, unreadCount: 0 };
                    }
                })
            );
            setChatUsersData(usersWithUnread.sort((a, b) => b.unreadCount - a.unreadCount));
        } catch {
            setChatUsersData([]);
        }
    };

    const handleUserChatSearch = (e) => {
        setSearchValue(e.target.value);
        fetchUsersData(e.target.value);
    };

    const fetchChatHistory = async (recipientId) => {
        try {
            const response = await api.get(`/chat/history/${recipientId}`, { headers });
            setMessages(response.data);
            await api.put(`/chat/markasread/${recipientId}`, {}, { headers });
            setChatUsersData((prev) =>
                prev.map((u) => (u.user_id === recipientId ? { ...u, unreadCount: 0 } : u))
            );
            updateNotification();
        } catch (err) {
            console.error(err);
        }
    };

    const handleChatClick = useCallback((ext, name, id) => {
        setReceipitentId(id);
        setUserChatInfo(`${name} — ${ext}`);
        fetchChatHistory(id);
        window.dispatchEvent(new CustomEvent('activeChatUser', { detail: id }));
    }, []);

    useEffect(() => {
        return () => window.dispatchEvent(new CustomEvent('activeChatUser', { detail: '' }));
    }, []);

    const emitMessage = useCallback(
        (message) => {
            if (!socket?.connected) {
                const q = getQueue();
                setQueue([...q, message]);
                setMessages((prev) => [...prev, { ...message, pending: true }]);
                setNewMessage('');
                return;
            }
            setMessages((prev) => [...prev, { ...message, pending: true }]);
            setNewMessage('');
            socket.emit('SEND_MESSAGE', message, (ack) => {
                if (ack?.success) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.tempId === message.tempId
                                ? { ...ack.data, pending: false, failed: false }
                                : m
                        )
                    );
                } else {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.tempId === message.tempId
                                ? { ...m, pending: false, failed: true }
                                : m
                        )
                    );
                    setNotificationMessage('Message failed to send. Tap to retry.');
                    setOpenSnackbar(true);
                }
            });
        },
        [socket]
    );

    const handleSendMessage = useCallback(async () => {
        if (!newMessage.trim() || !receipitentId) return;
        const message = {
            tempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
            sender_id: user_id,
            receiver_id: receipitentId,
            chat: newMessage,
            chat_time: new Date().toISOString(),
            read_status: false,
        };
        emitMessage(message);
        try {
            await api.put(`/chat/markasread/${receipitentId}`, {}, { headers });
        } catch (e) {
            console.error(e);
        }
        updateNotification();
    }, [socket, newMessage, receipitentId, user_id, emitMessage]);

    const handleRetryMessage = useCallback(
        (failedMsg) => {
            setMessages((prev) =>
                prev.map((m) =>
                    m.tempId === failedMsg.tempId
                        ? { ...m, failed: false, pending: true }
                        : m
                )
            );
            emitMessage(failedMsg);
        },
        [emitMessage]
    );

    useEffect(() => {
        if (!socket) return;
        const reconnect = () => {
            const queue = getQueue();
            if (!queue.length) return;
            queue.forEach((msg) => {
                socket.emit('SEND_MESSAGE', msg, (ack) => {
                    if (ack?.success) {
                        setQueue(getQueue().filter((m) => m.tempId !== msg.tempId));
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.tempId === msg.tempId
                                    ? { ...ack.data, pending: false, failed: false }
                                    : m
                            )
                        );
                    }
                });
            });
        };
        socket.on('connect', reconnect);
        return () => socket.off('connect', reconnect);
    }, [socket]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleNewMessage = useCallback(
        (message) => {
            const cur = receipitentIdRef.current;
            if (message.receiver_id === user_id && message.sender_id !== cur) {
                api.get(`/user/getuser/${message.sender_id}`, {
                    headers,
                    withCredentials: true,
                })
                    .then((res) => {
                        setNotificationMessage(`New message from ${res.data.user_name}`);
                        setOpenSnackbar(true);
                        moveUserToTop(message.sender_id);
                    })
                    .catch(console.error);
            }
            if (
                (message.receiver_id === cur && message.sender_id === user_id) ||
                (message.sender_id === cur && message.receiver_id === user_id)
            ) {
                setMessages((prev) => {
                    const idx = prev.findIndex(
                        (m) =>
                            m.tempId &&
                            m.sender_id === message.sender_id &&
                            m.chat === message.chat
                    );
                    if (idx !== -1) {
                        const copy = [...prev];
                        copy[idx] = { ...message, pending: false };
                        return copy;
                    }
                    return [...prev, message];
                });
                moveUserToTop(message.sender_id);
            }
        },
        [user_id]
    );

    useEffect(() => {
        if (!socket) return;
        socket.on('NEW_MESSAGE', handleNewMessage);
        return () => socket.off('NEW_MESSAGE', handleNewMessage);
    }, [socket, handleNewMessage]);

    useEffect(() => {
        fetchUsersData();
    }, []);

    const formatMessageDate = (d) => {
        const dt = new Date(d);
        if (isToday(dt)) return 'Today';
        if (isYesterday(dt)) return 'Yesterday';
        if (differenceInDays(new Date(), dt) < 7) return format(dt, 'EEEE');
        return format(dt, 'dd/MM/yyyy');
    };

    const updateNotification = () => {
        window.dispatchEvent(
            new CustomEvent('updateMainNotificationCount', {
                detail: 'refreshCurrentNotification',
            })
        );
    };

    /* ── status icon ─────────────────────────────────────────────────────── */
    const StatusIcon = ({ msg }) => {
        if (msg.pending)
            return <AccessTimeRounded sx={{ fontSize: 14, opacity: 0.45 }} />;
        if (msg.failed)
            return <ErrorOutlineRounded sx={{ fontSize: 14, color: 'error.main' }} />;
        if (msg.read_status)
            return <DoneAllRounded sx={{ fontSize: 14, color: '#53bdeb' }} />;
        return <DoneRounded sx={{ fontSize: 14, opacity: 0.45 }} />;
    };

    const callNow = (ext) => {
    if (ext) window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: ext }));};

    /* ═══════════════════════════════════════════════════════════════════════
       RENDER
    ═══════════════════════════════════════════════════════════════════════ */
    return (
        <>
            <CssBaseline />

            {/* ── page title ─────────────────────────────────────────────── */}
            <Typography
                variant="h5"
                sx={{
                    mb: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <ChatBubbleOutlineRounded sx={{ fontSize: 26 }} />
                Chat
            </Typography>

            {/* ── main container ──────────────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    height: 'calc(100vh - 130px)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: (t) =>
                        t.palette.mode === 'dark'
                            ? '0 0 0 1px rgba(255,255,255,0.04)'
                            : '0 4px 24px rgba(0,0,0,0.06)',
                }}
            >
                {/* ─── SIDEBAR ────────────────────────────────────────────── */}
                <Box
                    sx={{
                        width: 340,
                        minWidth: 340,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {/* sidebar header */}
                    <Box sx={{ p: 2.5, pb: 1.5 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search conversations…"
                            value={searchUserValue}
                            onChange={handleUserChatSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRounded
                                            sx={{ fontSize: 20, opacity: 0.4 }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    bgcolor: 'action.hover',
                                    '& fieldset': { border: 'none' },
                                    '&:hover': { bgcolor: 'action.selected' },
                                    '&.Mui-focused': {
                                        bgcolor: 'background.paper',
                                        boxShadow: (t) =>
                                            `0 0 0 2px ${t.palette.primary.main}33`,
                                    },
                                },
                            }}
                        />
                    </Box>

                    <Divider />

                    {/* user list */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            py: 0.5,
                            '&::-webkit-scrollbar': { width: 4 },
                            '&::-webkit-scrollbar-thumb': {
                                borderRadius: 4,
                                bgcolor: 'divider',
                            },
                        }}
                    >
                        {chatUsersData.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 8, opacity: 0.3 }}>
                                <Typography variant="body2">
                                    No conversations
                                </Typography>
                            </Box>
                        )}

                        {chatUsersData.map((userchat) => {
                            const isActive = userchat.user_id === receipitentId;
                            const hasUnread = userchat.unreadCount > 0;

                            return (
                                <Box
                                    key={userchat.user_id}
                                    onClick={() =>
                                        handleChatClick(
                                            userchat.user_extension,
                                            userchat.user_name,
                                            userchat.user_id
                                        )
                                    }
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        px: 2,
                                        py: 1.25,
                                        mx: 1,
                                        my: 0.25,
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        bgcolor: isActive
                                            ? 'action.selected'
                                            : 'transparent',
                                        '&:hover': {
                                            bgcolor: isActive
                                                ? 'action.selected'
                                                : 'action.hover',
                                        },
                                    }}
                                >
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        variant="dot"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                bgcolor: '#44b700',
                                                boxShadow: '0 0 0 2px',
                                                color: '#44b700',
                                            },
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                fontSize: 15,
                                                fontWeight: 600,
                                                bgcolor: stringToColor(
                                                    userchat.user_name || '?'
                                                ),
                                            }}
                                        >
                                            {initials(userchat.user_name)}
                                        </Avatar>
                                    </Badge>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: hasUnread ? 700 : 500,
                                                lineHeight: 1.3,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {userchat.user_name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                opacity: 0.45,
                                                fontWeight: hasUnread
                                                    ? 600
                                                    : 400,
                                            }}
                                        >
                                            Ext. {userchat.user_extension}
                                        </Typography>
                                    </Box>

                                    {hasUnread && (
                                        <Box
                                            sx={{
                                                minWidth: 22,
                                                height: 22,
                                                borderRadius: '11px',
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                px: 1,
                                            }}
                                        >
                                            {userchat.unreadCount}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* ─── CHAT PANEL ─────────────────────────────────────────── */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* chat header */}
                    <Box
                        sx={{
                            px: 3,
                            py: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            minHeight: 64,
                        }}
                    >
                        {receipitentId ? (
                            <>
                                <Avatar
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        fontSize: 14,
                                        fontWeight: 600,
                                        bgcolor: stringToColor(
                                            userChatInfo.split('—')[0]?.trim() ||
                                                '?'
                                        ),
                                    }}
                                >
                                    {initials(
                                        userChatInfo.split('—')[0]?.trim()
                                    )}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {userChatInfo
                                            .split('—')[0]
                                            ?.trim()}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.45 }}
                                    >
                                        {userChatInfo
                                            .split('—')[1]
                                            ?.trim()}
                                    </Typography>
                                </Box>

                                {/* ── phone / call button ─────────────────── */}
                                <Tooltip arrow>
                                    <IconButton
                                        onClick={() => {
                                            const ext = userChatInfo
                                                .split('—')[1]
                                                ?.trim();
                                            if (ext) {callNow(ext)}
                                        }}
                                        sx={{
                                            color: 'primary.main',
                                            bgcolor: 'action.hover',
                                            '&:hover': {
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText',
                                                transform: 'scale(1.08)',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <PhoneRounded />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <Typography
                                variant="subtitle1"
                                sx={{ opacity: 0.35, fontWeight: 500 }}
                            >
                                Select a conversation
                            </Typography>
                        )}
                    </Box>

                    {/* messages area */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            px: 3,
                            py: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.25,
                            '&::-webkit-scrollbar': { width: 5 },
                            '&::-webkit-scrollbar-thumb': {
                                borderRadius: 4,
                                bgcolor: 'divider',
                            },
                        }}
                    >
                        {!receipitentId && (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0.15,
                                    gap: 2,
                                }}
                            >
                                <ChatBubbleOutlineRounded
                                    sx={{ fontSize: 72 }}
                                />
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500 }}
                                >
                                    Start a conversation
                                </Typography>
                            </Box>
                        )}

                        {messages.map((message, index) => {
                            const isMine = message.sender_id === user_id;
                            const showDate =
                                index === 0 ||
                                formatMessageDate(message.chat_time) !==
                                    formatMessageDate(
                                        messages[index - 1]?.chat_time
                                    );

                            return (
                                <React.Fragment
                                    key={
                                        message.chatId ||
                                        message.tempId ||
                                        index
                                    }
                                >
                                    {/* date separator */}
                                    {showDate && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                my: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '20px',
                                                    bgcolor: 'action.hover',
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    opacity: 0.6,
                                                }}
                                            >
                                                {formatMessageDate(
                                                    message.chat_time
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    <Fade in timeout={250}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: isMine
                                                    ? 'flex-end'
                                                    : 'flex-start',
                                                mb: 0.5,
                                            }}
                                            ref={
                                                index ===
                                                messages.length - 1
                                                    ? messagesEndRef
                                                    : null
                                            }
                                            onClick={() =>
                                                message.failed &&
                                                handleRetryMessage(message)
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    maxWidth: '62%',
                                                    px: 2,
                                                    py: 1.25,
                                                    borderRadius: isMine
                                                        ? '20px 20px 6px 20px'
                                                        : '20px 20px 20px 6px',
                                                    bgcolor: message.failed
                                                        ? 'error.main'
                                                        : isMine
                                                        ? (t) =>
                                                              t.palette.mode ===
                                                              'dark'
                                                                  ? 'rgba(144, 202, 249, 0.14)'
                                                                  : 'rgba(25, 118, 210, 0.10)'
                                                        : 'action.hover',
                                                    color: message.failed
                                                        ? 'error.contrastText'
                                                        : isMine
                                                        ? 'text.primary'
                                                        : 'text.primary',
                                                    opacity: message.pending
                                                        ? 0.6
                                                        : 1,
                                                    cursor: message.failed
                                                        ? 'pointer'
                                                        : 'default',
                                                    transition:
                                                        'all 0.2s ease',
                                                    boxShadow: isMine
                                                        ? '0 1px 3px rgba(0,0,0,0.06)'
                                                        : 'none',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak:
                                                            'break-word',
                                                        lineHeight: 1.55,
                                                    }}
                                                >
                                                    {message.chat}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems:
                                                            'center',
                                                        justifyContent:
                                                            'flex-end',
                                                        gap: 0.5,
                                                        mt: 0.75,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            opacity: 0.55,
                                                            fontSize:
                                                                '0.65rem',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {new Date(
                                                            message.chat_time
                                                        ).toLocaleTimeString(
                                                            [],
                                                            {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            }
                                                        )}
                                                    </Typography>
                                                    {isMine && (
                                                        <StatusIcon
                                                            msg={message}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Fade>
                                </React.Fragment>
                            );
                        })}
                    </Box>

                    {/* input area */}
                    {receipitentId && (
                        <Box
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                            }}
                        >
                            {/* main input row */}
                            <Box
                                sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    gap: 1,
                                }}
                            >
                                <Tooltip title="Emoji" arrow>
                                    <IconButton
                                        onClick={() =>
                                            setOpen((p) => !p)
                                        }
                                        sx={{
                                            mb: 0.5,
                                            opacity: 0.5,
                                            transition: 'all 0.2s ease',
                                            color: open
                                                ? 'primary.main'
                                                : 'inherit',
                                            '&:hover': {
                                                opacity: 1,
                                                bgcolor: 'action.hover',
                                                transform: 'scale(1.1)',
                                            },
                                        }}
                                    >
                                        <SentimentSatisfiedAltRounded />
                                    </IconButton>
                                </Tooltip>

                                <TextField
                                    multiline
                                    maxRows={4}
                                    placeholder="Type a message…"
                                    value={newMessage}
                                    onChange={(e) =>
                                        setNewMessage(e.target.value)
                                    }
                                    onKeyDown={handleKeyPress}
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '24px',
                                            bgcolor: 'action.hover',
                                            '& fieldset': { border: 'none' },
                                            '&:hover': {
                                                bgcolor: 'action.selected',
                                            },
                                            '&.Mui-focused': {
                                                bgcolor: 'background.paper',
                                                boxShadow: (t) =>
                                                    `0 0 0 2px ${t.palette.primary.main}33`,
                                            },
                                            px: 2,
                                            py: 1,
                                        },
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={handleSendMessage}
                                    disabled={!newMessage.trim()}
                                    sx={{
                                        minWidth: 46,
                                        width: 46,
                                        height: 46,
                                        borderRadius: '50%',
                                        mb: 0.5,
                                        p: 0,
                                        boxShadow: 'none',
                                        transition:
                                            'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        '&:hover': {
                                            boxShadow: 'none',
                                            transform: 'scale(1.08)',
                                        },
                                        '&:disabled': {
                                            bgcolor:
                                                'action.disabledBackground',
                                            color: 'action.disabled',
                                        },
                                    }}
                                >
                                    <SendRounded
                                        sx={{ fontSize: 20 }}
                                        onClick={() => setOpen(false)}
                                    />
                                </Button>
                            </Box>

                            {/* emoji picker — slides in below the input */}
                            <Box
                                sx={{
                                    maxHeight: open ? 380 : 0,
                                    overflow: 'hidden',
                                    transition:
                                        'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            >
                                <Box sx={{ px: 2.5, pb: 1.5 }}>
                                    <Box
                                        sx={{
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            boxShadow:
                                                '0 8px 32px rgba(0,0,0,0.15)',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <EmojiPicker
                                            onEmojiClick={handleEmojiClick}
                                            width="100%"
                                            height={340}
                                            theme={
                                                document.documentElement.getAttribute(
                                                    'data-mui-color-scheme'
                                                ) === 'dark'
                                                    ? 'dark'
                                                    : 'light'
                                            }
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* snackbar */}
            <Snackbar
                open={openSnackbar}
                anchorOrigin={{ vertical, horizontal }}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity="info"
                    variant="filled"
                    sx={{ borderRadius: '12px' }}
                >
                    {notificationMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Chat;